import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, Typography, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";
import InputFieldAutoComplete from "../../../common/InputFieldAutoComplete";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { CLIENTS } from "../../../../constants/value";
import { __portinSelectel, __getPlans } from "../../../../api/sim";

const COLUMN_COUNT = 13;

const NETWORK_OPTIONS = [
  { value: "verizon", label: "Verizon" },
  { value: "att", label: "AT&T" },
];

// Shown to the user as a concrete example so the CSV format is unambiguous.
// The plan itself is picked from the dropdown above (per plan_soc) - Selectel
// resolves the actual 4G/5G offer ID from this ESN automatically at submit
// time, so it never needs to be typed in here.
const SAMPLE_LINE = [
  "358240051111110",
  "89148000010413033010",
  "John",
  "Doe",
  "123 Main St",
  "Houston",
  "TX",
  "77001",
  "7135551234",
  "ACC123456",
  "pw123",
  "John Doe",
  "AT&T",
].join(",");

const PortinForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [plans, setPlans] = useState([]);
  const { setAlertMessage } = useAlert();
  const initialData = { plan_soc: "", network: "verizon" };
  const [formData, setFormData] = useState(null);
  const [response, call, loading] = useAxios({
    ...__portinSelectel,
  });
  const [planResponse, planCall, planLoading] = useAxios({
    ...__getPlans,
  });

  const nav = useNavigate();

  const lineValidationSchema = yup.object({
    esn: yup.string("esn").required(),
    iccid: yup
      .string("iccid")
      .min(19, "Should be exactly 19 or 20 digits long")
      .max(20, "Should be exactly 19 or 20 digits long")
      .required(),
    first_name: yup.string("first_name").required(),
    last_name: yup.string("last_name").required(),
    street: yup.string("street").required(),
    city: yup.string("city").required(),
    state: yup.string("state").required(),
    zipcode: yup
      .string("zipcode")
      .test("Digits only", "The field should have 5 digits only", (value) =>
        /^\d{5}$/.test(value)
      )
      .required(),
    portin_msisdn: yup
      .string("portin_msisdn")
      .min(10, "Should be exactly 10 digits long")
      .max(10, "Should be exactly 10 digits long")
      .required(),
    osp_account_number: yup.string("osp_account_number").required(),
    osp_account_password: yup.string("osp_account_password").required(),
    authorized_name: yup.string("authorized_name").required(),
    current_carrier: yup.string("current_carrier").required(),
  });

  useEffect(() => {
    planCall({ params: { client: CLIENTS.selectel } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (planResponse && planResponse.status) {
      setPlans(planResponse.data);
    }
  }, [planResponse]);

  useEffect(() => {
    if (formData) {
      call({
        data: {
          network: formData.network,
          plan_soc: formData.plan_soc,
          lines: [...linesData],
        },
      });
    }
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "Portin queue started" });
      setLinesData([]);
      nav("/selectel/portin");
    }
    formik.setSubmitting(false);
  }, [response]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];

    if (!data) {
      return errors;
    }

    const lines = data.split("\n").filter((line) => line.trim());
    lines.some((line, idx) => {
      const lineData = line.split(",");
      if (lineData.length !== COLUMN_COUNT) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      try {
        const [
          esn,
          iccid,
          first_name,
          last_name,
          street,
          city,
          state,
          zipcode,
          portin_msisdn,
          osp_account_number,
          osp_account_password,
          authorized_name,
          current_carrier,
        ] = lineData.map((v) => v.trim());

        const obtainedData = {
          esn,
          iccid,
          first_name,
          last_name,
          street,
          city,
          state,
          zipcode,
          portin_msisdn,
          osp_account_number,
          osp_account_password,
          authorized_name,
          current_carrier,
        };
        lineValidationSchema.validateSync(obtainedData);

        formatedLines.push(obtainedData);
      } catch (err) {
        console.log(err);
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      setFormData({ plan_soc: values.plan_soc, network: values.network });
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };
  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors.lines,
          upload: "Please input a csv file",
        });
        return;
      }

      handleParse(inputFile);
    }
  };

  if (loading || planLoading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Portin new Selectel lines"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="network" sx={{ position: "static" }}>
                Network
              </InputLabel>
              <RadioGroup
                row
                name="network"
                value={formik.values.network}
                onChange={(e) => formik.setFieldValue("network", e.target.value)}
              >
                {NETWORK_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={350}>
            <FormControl
              error={
                formik.touched.plan_soc && Boolean(formik.errors.plan_soc)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputFieldAutoComplete
                isSelect
                options={[
                  ...plans.reduce((acc, plan) => {
                    const val = { value: plan.id, label: plan.name };
                    acc.push(val);
                    return acc;
                  }, []),
                ]}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                name="plan_soc"
                label="Plan"
                id="plan_soc"
                onChange={(e, value) =>
                  formik.setFieldValue("plan_soc", value?.value || null)
                }
              />
              <FormHelperText>
                4G/5G will be applied based on the device ESN.
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.upload)}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors.upload) ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="lines">
              {"Add in the format:"}
              <br></br>
              {
                "ESN,ICCID,FirstName,LastName,Street,City,State,Zipcode,PortinMSISDN,OSPAccountNumber,OSPAccountPassword,AuthorizedName,CurrentCarrier"
              }
              <br></br>
            </InputLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {"Example: "}
              <code>{SAMPLE_LINE}</code>
            </Typography>
            <FormControl
              error={Boolean(formik.errors.lines)}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={`Add in the format\nESN,ICCID,FirstName,LastName,Street,City,State,Zipcode,PortinMSISDN,OSPAccountNumber,OSPAccountPassword,AuthorizedName,CurrentCarrier\n\nExample:\n${SAMPLE_LINE}`}
                style={{ minWidth: 550 }}
                minRows={5}
                name="lines"
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
              />
              {Boolean(formik.errors.lines) ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  formik.errors.lines ||
                  !formik.values.plan_soc ||
                  !linesData.length
                }
                onClick={() =>
                  !loading &&
                  !formik.isSubmitting &&
                  !planLoading &&
                  formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Portin new lines"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default PortinForm;
